// The two reads the "Adaugă mișcare de stoc" dialog makes (spec 054 T035),
// typed to contracts/bff-stock-reporting.md: one search over medicines and the
// lots already reported at the location (research R5), and the lot list of one
// medicine at that location, which is also where the dialog's "Stoc anterior"
// comes from (R6).
//
// Both take no location too (owner decision, 2026-09-15; spec 054 T102): a
// report may take rows before its location is chosen. The bff then answers the
// search from the catalogue alone, with no lots per hit, and the lot list with
// none, since a lot's stock belongs to a location (research R2).
import { apiClient } from '../../app/api-client';

/** The bff refuses a shorter query with 400 QUERY_TOO_SHORT, so none is sent. */
export const REPORT_SEARCH_MIN_LENGTH = 2;
/** The wireframe's short list; the true total drives "Afișate 8 din N". */
export const REPORT_SEARCH_MAX_HITS = 8;

export type ReportSearchField =
  | 'lot'
  | 'code'
  | 'name'
  | 'dci'
  | 'form'
  | 'dose'
  | 'volume'
  | 'division'
  | 'producer'
  | 'country'
  | 'atc'
  | 'barcode';

export type ReportSearchCodeKind = 'AUTHORIZED' | 'UNAUTHORIZED' | 'BARCODE';

/** A lot of the hit whose number matched the query, with its stock at the location. */
export interface MatchedLot {
  lot: string;
  /** `YYYY-MM-DD`, the latest expiry reported for the lot. */
  expiry: string | null;
  /** 'DAY' | 'MONTH': how the lot's expiry was reported; it decides how the date is shown. */
  expiryPrecision?: string;
  stock: number;
}

export interface ReportSearchHit {
  /** The code `core.stock.medicine_code` holds; what a row is filed under. */
  code: string;
  /** The identity's parts joined with " · ". */
  medicineLabel: string;
  /** The brand, or the DCI when the code has none. */
  name: string;
  dci: string | null;
  form: string | null;
  dose: string | null;
  volume: string | null;
  division: string | null;
  producer: string | null;
  country: string | null;
  atc: string | null;
  /** Caiet UC50 step 4 searches by barcode too. */
  barcode: string | null;
  /**
   * What the code is: a registration (authorised or not) or a barcode, which has
   * no authorisation of its own (BUG-1-044). Absent from a bff that predates it,
   * and then read as a registration.
   */
  kind?: ReportSearchCodeKind;
  authorised: boolean;
  /** `YYYY-MM-DD`; null for an unauthorised code or an expiry the catalog does not state. */
  authorisationExpiry: string | null;
  /**
   * `YYYY-MM-DD`: the last day an authorised code stays reportable once its
   * authorisation has expired — the expiry plus the configured grace period
   * (caiet UC20-10, 12 months by default). Null with no expiry to count from.
   */
  reportableUntil: string | null;
  /** False draws "inactiv": reportable only on its existing lots (UC50-01). */
  active: boolean;
  /** Which fields matched, e.g. `["name", "dose"]`. */
  matched: ReportSearchField[];
  lots: {
    /** Lots whose number matched the query, at most 3 shown. */
    matched: MatchedLot[];
    matchedTotal: number;
    /** How many lots of this code were ever reported at the location. */
    reportedCount: number;
  };
}

export interface ReportSearchResponse {
  /** The true count of matching codes, never capped. */
  total: number;
  hits: ReportSearchHit[];
}

/** One lot of a medicine, as the lot field and the previous stock read it. */
export interface ReportedLot {
  lot: string;
  expiry: string | null;
  /** 'DAY' | 'MONTH': how the lot's expiry was reported; it decides how the date is shown. */
  expiryPrecision?: string;
  /** SUM(quantity_signed) at the location; may be 0 or below (data-model.md). */
  stock: number;
  /** A movement exists for this lot at the location: an existing lot, not a new one. */
  reportedHere: boolean;
  /** Reported at another location the caller reaches (research R2, CF-43). */
  knownElsewhere: boolean;
}

const NO_LOTS: ReportSearchHit['lots'] = { matched: [], matchedTotal: 0, reportedCount: 0 };

// A hit is drawn part by part, so a part the bff left out reads as empty rather
// than breaking the row it belongs to.
function normalizeHit(hit: ReportSearchHit): ReportSearchHit {
  return {
    ...hit,
    matched: hit.matched ?? [],
    lots: hit.lots
      ? {
          matched: hit.lots.matched ?? [],
          matchedTotal: hit.lots.matchedTotal ?? 0,
          reportedCount: hit.lots.reportedCount ?? 0,
        }
      : NO_LOTS,
  };
}

export async function searchReport(
  q: string,
  siteId: string | null,
): Promise<ReportSearchResponse> {
  const query = q.trim();
  if (query.length < REPORT_SEARCH_MIN_LENGTH) return { total: 0, hits: [] };
  const { data } = await apiClient.get<ReportSearchResponse>('/stock/report-search', {
    params: siteId ? { q: query, siteId } : { q: query },
  });
  return {
    total: data.total ?? 0,
    hits: (data.hits ?? []).slice(0, REPORT_SEARCH_MAX_HITS).map(normalizeHit),
  };
}

export async function listLots(
  siteId: string | null,
  medicineCode: string,
): Promise<ReportedLot[]> {
  const { data } = await apiClient.get<{ lots?: ReportedLot[] }>('/stock/lots', {
    params: siteId ? { siteId, medicineCode } : { medicineCode },
  });
  return data.lots ?? [];
}
